import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {Attachment02Icon} from '@/components/ui/icon/Icon'
import {FileUploader} from '@/components/commons/file-upload/FileUpload'
import {acceptedFileTypes} from '@/features/users/utils'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {z} from 'zod'
import {useController, useFormContext} from 'react-hook-form'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useUploadDocument} from '@/services/useUploadDocument'
import {toast} from '@/utilities/toast/toast'
import {useDeleteDocument} from '@/services/useDeleteDocument'
import {Text} from '@/components/ui/text/Text'

const DocumentAttachmentSchema = MedicalReportFieldsSchema.pick({
    documentAttachment: true
})
export type DocumentAttachmentSchema = z.infer<typeof DocumentAttachmentSchema>

export const MedicalReportUploadDocumentField = () => {
    const form = useFormContext<DocumentAttachmentSchema>()
    const {t} = useTranslation()

    const documentControl = useController({control: form.control, name: 'documentAttachment'})
    const uploadDocument = useUploadDocument({
        onSuccess: response => {
            documentControl.field.onChange(response)
            toast.success(t('commons:upload_success'))
        },
        onError: () => toast.error(t('commons:upload_error'))
    })
    const deleteDocument = useDeleteDocument({
        onSuccess: () => {
            documentControl.field.onChange(null)
        }
    })

    return (
        <Flexbox fullWidth direction="column" gap={1.5}>
            <Text palette="neutral" shade="950" size="textSm" weight="500">
                {t('commons:file')}
            </Text>
            <FileUploader
                document={documentControl.field.value}
                isLoading={uploadDocument.isPending || deleteDocument.isPending}
                inputFileProps={{
                    acceptedFiles: acceptedFileTypes,
                    maxSizeInMB: 8,
                    placeholder: t('projects:attach_file'),
                    onAcceptFiles: file => {
                        const formData = new FormData()
                        formData.append(`file`, file)
                        uploadDocument.mutate(formData)
                    },
                    typeIcon: <Attachment02Icon size={15} />,
                    hideButton: true,
                    width: '100%'
                }}
                onDeleteFile={() => deleteDocument.mutate(documentControl.field.value?.id)}
                dropDownMenuProps={{
                    triggerWidth: '100%'
                }}
            />
            {form.formState.errors.documentAttachment?.message && <InputHelpText error={t('errors:required')} />}
        </Flexbox>
    )
}
